import { centsToDollars } from '../../helpers/money';
import type { GetMarketplacePropertyDetailsQuery } from './__generated__/marketplace-property.zeus';

export const mapMarketplaceProperty = (data: GetMarketplacePropertyDetailsQuery) => {
  const details = data.marketplace.listingDetails;
  if (!details) {
    return null;
  }
  const listing = details.summary;
  const availability = details.availability;
  if (!listing.slug) {
    return null;
  }

  const summaryListingState = listing.listingState;
  const currentListingState = availability?.state ?? null;
  const hasKnownCurrentAvailability =
    currentListingState != null && currentListingState !== 'UNKNOWN';
  if (
    (!hasKnownCurrentAvailability && summaryListingState === 'UNKNOWN') ||
    (currentListingState === 'UNKNOWN' && listing.displayBadge !== 'TRADITIONAL')
  ) {
    return null;
  }
  const resolvedListingState = hasKnownCurrentAvailability
    ? currentListingState
    : summaryListingState;

  return {
    addressToken: listing.addressId,
    annualPropertyTax: centsToDollars(details.annualPropertyTaxCents),
    bathrooms: listing.bathrooms,
    bedrooms: listing.bedrooms,
    buyDirectPrice: centsToDollars(details.buyDirect?.priceCents),
    buyDirectSavings: centsToDollars(details.buyDirect?.savingsCents),
    canSelfTour:
      hasKnownCurrentAvailability && availability ? availability.canSelfTour : listing.canSelfTour,
    city: listing.city,
    daysOnMarket: details.daysOnMarket,
    description: details.description,
    displayBadge:
      hasKnownCurrentAvailability && availability
        ? availability.displayBadge
        : listing.displayBadge,
    dwellingType: listing.dwellingType,
    estimatedMonthlyPayment: centsToDollars(listing.estimatedMonthlyPaymentCents),
    exteriorStories: details.exteriorStories,
    floorPlanPhotos: details.floorPlanPhotos.map((photo) => ({
      label: photo.label,
      url: photo.url,
    })),
    garageSpaces: details.garageSpaces,
    hasCurrentAvailability: availability != null,
    hasKnownCurrentAvailability,
    hasSellerDisclosures: details.hasSellerDisclosures,
    tourBookingListingId: details.tourBookingListingId,
    hoaMonthlyDues: centsToDollars(details.hoaMonthlyDuesCents),
    kitchenCountertop: details.kitchenCountertop,
    latitude: listing.latitude,
    listPrice: centsToDollars(listing.listPriceCents),
    listingHistory: details.listingHistory ?? [],
    listingState: resolvedListingState,
    buyDirectStatus: details.buyDirect?.status ?? null,
    longitude: listing.longitude,
    primaryBedroomLocation: details.primaryBedroomLocation,
    photos: details.photos.map((photo) => photo.mediumUrl),
    photosLg: details.photos.map((photo) => photo.largeUrl),
    photosSm: details.photos.map((photo) => photo.smallUrl),
    photosXl: details.photos.map((photo) => photo.extraLargeUrl),
    photosXs: details.photos.map((photo) => photo.extraSmallUrl),
    pricePerSqFt: centsToDollars(listing.pricePerSquareFootCents),
    schoolDistricts: details.schoolDistricts ?? [],
    schools: (details.schools ?? []).map((school) => ({
      id: school.id,
      name: school.name,
      rating: school.rating,
    })),
    shouldShowGuarantee: details.shouldShowGuarantee,
    similarProperties: (details.similarListings ?? []).map((similar) => ({
      addressToken: similar.addressId,
      baths: similar.bathrooms,
      beds: similar.bedrooms,
      canSelfTour: similar.canSelfTour,
      city: similar.city,
      dwellingType: similar.dwellingType,
      fullAddress: similar.fullAddress,
      listPrice: centsToDollars(similar.listPriceCents),
      livingSquareFootage: similar.totalLivingSquareFootage,
      neighborhood: similar.neighborhood,
      photos: similar.photoUrls,
      slug: similar.slug,
    })),
    slug: listing.slug,
    sqFtLot: listing.lotSquareFootage,
    sqFtTotalLiving: listing.totalLivingSquareFootage,
    state: listing.state,
    street: listing.street,
    unitNumber: details.unitNumber,
    unitType: details.unitType,
    walkthroughVideos: (details.walkthroughVideos ?? []).map((video) => ({
      id: video.id,
      url: video.url,
    })),
    yearBuilt: listing.yearBuilt == null ? null : String(listing.yearBuilt),
    zip: listing.postalCode,
  };
};
